import { useContext, useState } from 'react';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import toast from 'react-hot-toast';
import { confirmCardSetup } from 'services/client/stripe/confirmCardSetup';
import { convertUnitPriceToFormattedPrice } from 'utils/shared/money/convertUnitPriceToFormattedPrice';
import { StripeContext, StripeProvider } from 'context/StripeContext';
import { useStripeSetupIntent } from 'hooks/useStripeSetupIntent';
import Calendar from 'svg/Calendar';
import Clock from 'svg/Clock';
import InfoCircle from 'svg/InfoCircle';
import ActivityBadge, { COURT_BOOKING_TYPE } from 'components/ActivityBadge';
import Button from 'components/Button';
import LoadingSkeleton from 'components/LoadingSkeleton';
import classNames from 'styles/utils/classNames';
import StepHeader from './StepHeader';
import { BookableCourt, BookingQuote } from '../types';

interface Props {
  onBack: () => void;
  venueName: string;
  venueImageUrl?: string | null;
  court: BookableCourt | null;
  quote: BookingQuote | null;
  isQuoteLoading?: boolean;
  /** `Apr 20 • Wed` */
  dateLabel: string;
  /** `3 – 5 PM` */
  timeLabel: string;
  /** The card is saved first; the server then authorizes it against the quote. */
  onPay: (paymentMethodId: string) => void | Promise<void>;
  isPaying?: boolean;
  className?: string;
}

const summaryRowClasses =
  'flex items-center justify-between typography-product-body text-color-text-lightmode-primary dark:text-color-text-darkmode-primary';

/** Step 3: review and pay. The card is saved via SetupIntent; the server holds the amount at checkout. */
export default function PayAndConfirmStep(props: Props) {
  return (
    <StripeProvider>
      <PaymentElements {...props} />
    </StripeProvider>
  );
}

function PaymentElements(props: Props) {
  const { stripe } = useContext(StripeContext);
  const { clientSecret, loadFailed } = useStripeSetupIntent();

  if (!stripe || !clientSecret) {
    return (
      <PayLayout {...props} isReady={false}>
        {loadFailed ? (
          <p className="typography-product-body text-color-error">
            We couldn&apos;t start a secure payment. Please try again.
          </p>
        ) : (
          <LoadingSkeleton count={3} />
        )}
      </PayLayout>
    );
  }

  return (
    <Elements stripe={stripe} options={{ clientSecret, loader: 'always' }}>
      <PayForm {...props} />
    </Elements>
  );
}

function PayForm(props: Props) {
  const stripe = useStripe();
  const elements = useElements();
  const [isSavingCard, setIsSavingCard] = useState(false);

  const handlePay = async () => {
    if (!stripe || !elements) {
      toast.error('Payment form is still loading');
      return;
    }
    setIsSavingCard(true);
    try {
      const card = await confirmCardSetup(stripe, elements);
      if (!card.ok) {
        toast.error(card.error);
        return;
      }
      await props.onPay(card.paymentMethodId);
    } finally {
      setIsSavingCard(false);
    }
  };

  return (
    <PayLayout {...props} isReady onPayClick={handlePay} isSavingCard={isSavingCard}>
      <PaymentElement />
    </PayLayout>
  );
}

function PayLayout({
  children,
  isReady,
  onPayClick,
  isSavingCard,
  onBack,
  venueName,
  venueImageUrl,
  court,
  quote,
  isQuoteLoading,
  dateLabel,
  timeLabel,
  isPaying,
  className,
}: Props & {
  children: React.ReactNode;
  isReady: boolean;
  onPayClick?: () => void;
  isSavingCard?: boolean;
}) {
  const rental = quote && convertUnitPriceToFormattedPrice(quote.rentalUnitAmount, quote.currency);
  const listRental =
    quote?.listRentalUnitAmount != null
      ? convertUnitPriceToFormattedPrice(quote.listRentalUnitAmount, quote.currency)
      : null;
  const fees = quote && convertUnitPriceToFormattedPrice(quote.feesUnitAmount, quote.currency);
  const total = quote && convertUnitPriceToFormattedPrice(quote.totalUnitAmount, quote.currency);

  return (
    <div className={classNames('flex flex-col', className)}>
      <StepHeader title="Pay and confirm" onBack={onBack} />

      <div className="flex flex-col gap-ds-xl overflow-y-auto px-ds-xl">
        <div className="flex gap-ds-md">
          {venueImageUrl && (
            <img
              src={venueImageUrl}
              alt=""
              aria-hidden
              className="size-[6.5rem] shrink-0 rounded-xl border border-color-border-input-lightmode object-cover dark:border-color-border-input-darkmode"
            />
          )}
          <div className="flex min-w-0 flex-col items-start justify-center gap-ds-sm">
            <div className="flex flex-col items-start gap-ds-xs">
              <ActivityBadge type={COURT_BOOKING_TYPE} size="sm" />
              <p className="typography-product-card-title text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
                {venueName}
              </p>
            </div>
            <div className="flex flex-col items-start gap-ds-xs">
              <span className="typography-product-caption flex items-center gap-ds-xs text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
                <Calendar className="h-5 w-5 shrink-0" />
                {dateLabel}
              </span>
              <span className="typography-product-caption flex items-center gap-ds-xs text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
                <Clock className="h-5 w-5 shrink-0" />
                {timeLabel}
              </span>
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-ds-md border-t border-color-border-card-lightmode pt-ds-xl dark:border-color-border-card-darkmode">
          {court && (
            <div className={summaryRowClasses}>
              <span>Court</span>
              <span>{court.name}</span>
            </div>
          )}
          {isQuoteLoading && !quote && <LoadingSkeleton count={2} />}
          {rental && (
            <div className={summaryRowClasses}>
              <span>Court rental</span>
              <span className="flex items-center gap-ds-sm">
                {listRental && (
                  <span className="text-color-text-lightmode-tertiary line-through dark:text-color-text-darkmode-tertiary">
                    {listRental.priceDisplay}
                  </span>
                )}
                {rental.priceDisplay}
              </span>
            </div>
          )}
          {fees && quote && quote.feesUnitAmount > 0 && (
            <div className={summaryRowClasses}>
              <span className="flex items-center gap-ds-xs">
                Taxes and credit cards fees
                <InfoCircle className="h-4 w-4 text-color-text-lightmode-icon dark:text-color-text-darkmode-icon" />
              </span>
              <span>{fees.priceDisplay}</span>
            </div>
          )}
          {total && (
            <div className="typography-product-subheading flex items-center justify-between text-color-text-brand">
              <span>Total</span>
              <span>{total.priceDisplay}</span>
            </div>
          )}
        </div>

        <div className="flex flex-col gap-ds-lg border-t border-color-border-card-lightmode pt-ds-xl dark:border-color-border-card-darkmode">
          <p className="typography-product-subheading text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
            Credit card
          </p>
          {children}
          <p className="typography-product-caption text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
            Your card is held now and charged only once the club confirms the booking.
          </p>
        </div>
      </div>

      <div className="p-ds-xl pt-ds-lg">
        <Button
          variant="brand"
          size="lg"
          label="Pay"
          loading={isSavingCard || isPaying}
          disabled={!isReady || !court || !quote}
          onClick={onPayClick}
        />
      </div>
    </div>
  );
}
